/**
 * Sound Notification Service — Digi Sahyadri HRM & WRMS
 * Synthesizes crisp, studio-grade notification and celebration chimes using the Web Audio API.
 * 100% self-contained: zero external network files, zero lag, reliable across modern browsers.
 */

let audioContext = null;

/**
 * Returns or initializes the AudioContext singleton
 */
const getAudioContext = () => {
  if (typeof window === 'undefined') return null;
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!audioContext) {
      audioContext = new AudioCtx();
    }
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }
    return audioContext;
  } catch (e) {
    console.warn('[soundService] AudioContext init error:', e);
    return null;
  }
};

// Auto-unlock AudioContext on first user interaction
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    try {
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume();
      }
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    } catch (_) {}
  };
  window.addEventListener('click', unlockAudio, { once: true, passive: true });
  window.addEventListener('keydown', unlockAudio, { once: true, passive: true });
  window.addEventListener('touchstart', unlockAudio, { once: true, passive: true });
}

const SOUND_STORAGE_KEY = 'hrm_notification_sound_enabled';

/**
 * Check if sound notifications are enabled in user preferences
 */
export const isSoundEnabled = () => {
  if (typeof window === 'undefined') return true;
  const val = localStorage.getItem(SOUND_STORAGE_KEY);
  return val === null ? true : val === 'true';
};

/**
 * Update sound notification preference
 */
export const setSoundEnabled = (enabled) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SOUND_STORAGE_KEY, enabled ? 'true' : 'false');
};

/**
 * Toggle sound notification preference and preview sound if turned on
 */
export const toggleSound = () => {
  const current = isSoundEnabled();
  const next = !current;
  setSoundEnabled(next);
  if (next) {
    playNotificationSound();
  }
  return next;
};

const VOLUME_STORAGE_KEY = 'hrm_notification_volume';

/**
 * Get current volume setting (default 0.85 = high & audible)
 */
export const getSoundVolume = () => {
  if (typeof window === 'undefined') return 0.85;
  const val = localStorage.getItem(VOLUME_STORAGE_KEY);
  return val !== null ? parseFloat(val) : 0.85;
};

/**
 * Set sound volume level (0.1 to 1.0)
 */
export const setSoundVolume = (vol) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(VOLUME_STORAGE_KEY, String(vol));
};

/**
 * Play standard request / action notification sound (chime)
 * Played when a new leave, gate pass, early leave request is submitted, approved, or received
 * Formatted as a rich, loud 3-tone alert chime (E5 -> A5 -> C#6)
 */
export const playNotificationSound = (customVolume) => {
  try {
    if (!isSoundEnabled()) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const volume = customVolume !== undefined ? customVolume : getSoundVolume();

    // Master Gain node set to high volume (0.85) for loud & crisp alert
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(Math.min(1.0, Math.max(0.1, volume)), now);
    masterGain.connect(ctx.destination);

    // Chime 1 - First tone (E5 - 659.25 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(659.25, now);
    gain1.gain.setValueAtTime(0.85, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(masterGain);
    osc1.start(now);
    osc1.stop(now + 0.35);

    // Chime 2 - Second harmonic tone (A5 - 880.00 Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(880.0, now + 0.09);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.setValueAtTime(0.9, now + 0.09);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    osc2.connect(gain2);
    gain2.connect(masterGain);
    osc2.start(now + 0.09);
    osc2.stop(now + 0.45);

    // Chime 3 - High presence bell tone (C#6 - 1108.73 Hz) for prominent audibility
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(1108.73, now + 0.18);
    gain3.gain.setValueAtTime(0, now);
    gain3.gain.setValueAtTime(0.95, now + 0.18);
    gain3.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
    osc3.connect(gain3);
    gain3.connect(masterGain);
    osc3.start(now + 0.18);
    osc3.stop(now + 0.65);
  } catch (err) {
    console.warn('[soundService] playNotificationSound error:', err);
  }
};

/**
 * Play celebration sound (joyful ascending chime fanfare)
 * Played when there is a Birthday or Work Anniversary today
 */
export const playCelebrationSound = (customVolume) => {
  try {
    if (!isSoundEnabled()) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;
    const volume = customVolume !== undefined ? customVolume : getSoundVolume();

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(Math.min(1.0, Math.max(0.1, volume)), now);
    masterGain.connect(ctx.destination);

    const notes = [
      { freq: 523.25, time: 0.00, dur: 0.22, type: 'triangle' }, // C5
      { freq: 659.25, time: 0.14, dur: 0.22, type: 'triangle' }, // E5
      { freq: 783.99, time: 0.28, dur: 0.25, type: 'sine' },     // G5
      { freq: 1046.50, time: 0.44, dur: 0.85, type: 'sine' },    // High C6
      { freq: 1318.51, time: 0.52, dur: 0.90, type: 'triangle' }, // High E6 sparkle
    ];

    notes.forEach((n) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = n.type || 'sine';
      osc.frequency.setValueAtTime(n.freq, now + n.time);
      gain.gain.setValueAtTime(0, now);
      gain.gain.setValueAtTime(0.85, now + n.time);
      gain.gain.exponentialRampToValueAtTime(0.001, now + n.time + n.dur);
      osc.connect(gain);
      gain.connect(masterGain);
      osc.start(now + n.time);
      osc.stop(now + n.time + n.dur);
    });
  } catch (err) {
    console.warn('[soundService] playCelebrationSound error:', err);
  }
};

const soundService = {
  playNotificationSound,
  playCelebrationSound,
  isSoundEnabled,
  setSoundEnabled,
  toggleSound,
  getSoundVolume,
  setSoundVolume,
};

export default soundService;
